"use client";

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type Dispatch,
  type SetStateAction,
} from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
} from "@react-google-maps/api";
import {
  Gauge,
  Zap,
  Navigation,
  Clock,
  MapPin,
  Plus,
  RotateCcw,
  BarChart3,
  Map as MapIcon,
  Loader2,
} from "lucide-react";
import { fetchBoatData, fetchBoatTabs } from "@/lib/range-calculator/api";
import {
  clampThrottle,
  clampThrottleToDataPoints,
  getRangeCalculatorMode,
  type RangeCalculatorDisplay,
} from "@/lib/range-calculator/calculator-state";
import {
  createBoatDataCache,
  getOrFetchBoatData,
} from "@/lib/range-calculator/boat-data-cache";
import { RangeEngine, haversineNm, computeRouteDistance } from "@/lib/range-calculator/range-engine";
import { PerformanceCharts } from "@/components/range-calculator/performance-charts";
import { ThrottleSlider } from "@/components/range-calculator/throttle-slider";
import type { BoatTab, BoatData, WaypointData } from "@/lib/range-calculator/types";
import { tracker } from "@/lib/tracker";

const NORFOLK_CENTER = { lat: 36.8871, lng: -76.2956 };
const NM_TO_METERS = 1852;

function formatWattage(w: number): string {
  return w >= 1000 ? `${(w / 1000).toFixed(1)} kW` : `${w.toFixed(0)} W`;
}

function formatDuration(hours: number): string {
  if (hours <= 0) return "--";
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
}

function formatBattery(wh: number): string {
  return wh >= 1000 ? `${(wh / 1000).toFixed(0)} kWh` : `${wh} Wh`;
}

function RangeMap({
  mapCenter,
  onMapClick,
  mapRef,
  circleCenter,
  displayRange,
  startLocation,
  setStartLocation,
  waypoints,
  setWaypoints,
}: {
  mapCenter: WaypointData;
  onMapClick: (e: google.maps.MapMouseEvent) => void;
  mapRef: { current: google.maps.Map | null };
  circleCenter: WaypointData;
  displayRange: number;
  startLocation: WaypointData | null;
  setStartLocation: Dispatch<SetStateAction<WaypointData | null>>;
  waypoints: WaypointData[];
  setWaypoints: Dispatch<SetStateAction<WaypointData[]>>;
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? "",
  });

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[700px]">
        <Loader2 className="h-8 w-8 text-em-blue animate-spin" />
      </div>
    );
  }

  return (
    <GoogleMap
      mapContainerClassName="w-full min-h-[700px]"
      center={mapCenter}
      zoom={10}
      onClick={onMapClick}
      onLoad={(map) => {
        mapRef.current = map;
      }}
      options={{
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      }}
    >
      <Circle
        center={circleCenter}
        radius={displayRange * NM_TO_METERS}
        options={{
          fillColor: "#3B82F6",
          fillOpacity: 0.08,
          strokeColor: "#3B82F6",
          strokeWeight: 2,
          strokeOpacity: 0.6,
          clickable: false,
        }}
      />

      {startLocation && (
        <Marker
          position={startLocation}
          draggable
          onDragEnd={(e) => {
            if (!e.latLng) return;
            setStartLocation({ lat: e.latLng.lat(), lng: e.latLng.lng() });
          }}
          label={{ text: "S", color: "white", fontWeight: "bold" }}
        />
      )}

      {waypoints.map((wp, i) => (
        <Marker
          key={i}
          position={wp}
          draggable
          onDragEnd={(e) => {
            if (!e.latLng) return;
            setWaypoints((prev) => {
              const next = [...prev];
              next[i] = { lat: e.latLng!.lat(), lng: e.latLng!.lng() };
              return next;
            });
          }}
          label={{ text: `${i + 1}`, color: "white", fontWeight: "bold" }}
        />
      ))}
    </GoogleMap>
  );
}

interface RangeCalculatorProps {
  datasetIds?: number[];
  display?: RangeCalculatorDisplay;
  initialTabs?: BoatTab[];
  initialBoatData?: BoatData[];
  showBoatSelector?: boolean;
}

function datasetKeyFor(datasetIds: number[] | undefined): string {
  if (datasetIds === undefined) return "all";
  return Array.from(
    new Set(
      datasetIds
        .map((id) => Math.trunc(id))
        .filter((id) => Number.isFinite(id) && id > 0)
    )
  ).join(",");
}

function tabsFromBoatData(boats: BoatData[]): BoatTab[] {
  return boats.map((boat) => ({
    id: boat.id,
    name: boat.name,
    sheetId: boat.sheetId,
    itemId: boat.itemId,
  }));
}

function DatasetLoadingState({
  tabs,
  selectedTab,
  display,
  loading,
  onSelect,
  showBoatSelector,
}: {
  tabs: BoatTab[];
  selectedTab: BoatTab;
  display: "compact" | "full";
  loading: boolean;
  onSelect: (tab: BoatTab) => void;
  showBoatSelector: boolean;
}) {
  const selector = (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Boat
      </label>
      <select
        value={selectedTab.sheetId}
        onChange={(event) => {
          const tab = tabs.find((candidate) => candidate.sheetId === Number(event.target.value));
          if (tab) onSelect(tab);
        }}
        className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50"
      >
        {tabs.map((tab) => (
          <option key={tab.sheetId} value={tab.sheetId}>
            {tab.name}
          </option>
        ))}
      </select>
    </div>
  );

  const status = (
    <div
      className="flex min-h-32 items-center justify-center text-sm text-muted-foreground"
      role="status"
    >
      {loading ? (
        <>
          <Loader2 className="mr-2 h-5 w-5 animate-spin text-em-blue" />
          Loading performance data…
        </>
      ) : (
        "Performance data is currently unavailable for this boat."
      )}
    </div>
  );

  if (display === "compact") {
    return (
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-5">
        {showBoatSelector && selector}
        {status}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[340px_1fr]">
      <div className="space-y-5 rounded-2xl border border-border bg-card p-5">
        {showBoatSelector && selector}
      </div>
      <div className="min-h-[320px] rounded-2xl border border-border bg-card">
        {status}
      </div>
    </div>
  );
}

export function RangeCalculator({
  datasetIds,
  display = "full",
  initialTabs: seededTabs,
  initialBoatData,
  showBoatSelector = true,
}: RangeCalculatorProps) {
  const calculatorMode = getRangeCalculatorMode(display);
  const hasDatasetFilter = datasetIds !== undefined;
  const datasetKey = datasetKeyFor(datasetIds);
  const initialTabs = seededTabs ?? (
    initialBoatData ? tabsFromBoatData(initialBoatData) : []
  );
  const seededSelectedTab = initialTabs[0] ?? null;
  const initialBoatCache = createBoatDataCache(initialBoatData);
  const initialBoat = seededSelectedTab
    ? initialBoatCache[String(seededSelectedTab.sheetId)] ?? null
    : null;
  const hasUsableInitialState = Boolean(seededSelectedTab && initialBoat);
  const initialSelectedTab = hasUsableInitialState ? seededSelectedTab : null;
  const [tabs, setTabs] = useState<BoatTab[]>(initialTabs);
  const [selectedTab, setSelectedTab] = useState<BoatTab | null>(initialSelectedTab);
  // A visitor picking a boat. The default boat shown on load is not a pick,
  // so it is set directly and never tracked.
  const selectBoat = useCallback((tab: BoatTab) => {
    tracker.event("calculator_boat_select", { boat: tab.name });
    setSelectedTab(tab);
  }, []);
  const [boatData, setBoatData] = useState<BoatData | null>(initialBoat);
  const [loadedDatasetKey, setLoadedDatasetKey] = useState<string | null>(
    hasUsableInitialState ? datasetKey : null
  );
  const [loadedBoatKey, setLoadedBoatKey] = useState<string | null>(
    initialBoat ? String(initialBoat.sheetId) : null
  );

  const [throttle, setThrottle] = useState(() =>
    initialBoat ? clampThrottleToDataPoints(50, initialBoat.dataPoints) : 50
  );
  const [selectedBattery, setSelectedBattery] = useState<number>(
    initialBoat?.batteries[0] ?? 0
  );

  const [viewMode, setViewMode] = useState<"map" | "charts">("map");
  const [mapCenter] = useState(NORFOLK_CENTER);
  const [startLocation, setStartLocation] = useState<WaypointData | null>(null);
  const [waypoints, setWaypoints] = useState<WaypointData[]>([]);
  const [interactionMode, setInteractionMode] = useState<"none" | "setStart" | "addWaypoint">("none");

  const boatDataCache = useRef(initialBoatCache);
  const mapRef = useRef<google.maps.Map | null>(null);
  const blueLineRef = useRef<google.maps.Polyline | null>(null);
  const redLineRef = useRef<google.maps.Polyline | null>(null);

  const loading = loadedDatasetKey !== datasetKey;
  const selectedBoatKey = selectedTab ? String(selectedTab.sheetId) : null;
  const loadingBoat = Boolean(selectedBoatKey && loadedBoatKey !== selectedBoatKey);

  // Standalone calculators can paint their selector from summaries immediately,
  // then load only the selected dataset. Embedded modules may pass their small,
  // eagerly validated dataset set so unresolved modules can stay fully hidden.
  useEffect(() => {
    let cancelled = false;
    const requestedDatasetIds = hasDatasetFilter && datasetKey
      ? datasetKey.split(",").map(Number)
      : hasDatasetFilter
        ? []
        : undefined;

    if (hasUsableInitialState) return;

    const dataPromise: Promise<{
      tabs: BoatTab[];
      cache: Record<string, BoatData>;
    }> = fetchBoatTabs(requestedDatasetIds).then((loadedTabs) => ({
      tabs: loadedTabs,
      cache: {},
    }));

    dataPromise
      .then(({ tabs: loadedTabs, cache }) => {
        if (cancelled) return;
        const firstTab = loadedTabs[0] ?? null;
        const firstBoat = firstTab ? cache[String(firstTab.sheetId)] ?? null : null;

        boatDataCache.current = cache;
        setTabs(loadedTabs);
        if (firstTab) {
          setSelectedTab(firstTab);
          setBoatData(firstBoat);
          setLoadedBoatKey(firstBoat ? String(firstBoat.sheetId) : null);
          setSelectedBattery(firstBoat?.batteries[0] ?? 0);
          if (firstBoat) {
            setThrottle((currentThrottle) =>
              clampThrottleToDataPoints(currentThrottle, firstBoat.dataPoints)
            );
          }
        } else {
          setSelectedTab(null);
          setBoatData(null);
          setLoadedBoatKey(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(error);
          setTabs([]);
          setSelectedTab(null);
          setBoatData(null);
          setLoadedBoatKey(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadedDatasetKey(datasetKey);
      });

    return () => {
      cancelled = true;
    };
  }, [datasetKey, hasDatasetFilter, hasUsableInitialState]);

  // Load boat data when tab changes
  useEffect(() => {
    if (!selectedTab) return;

    let cancelled = false;
    const cacheKey = String(selectedTab.sheetId);

    const dataPromise = getOrFetchBoatData(
      selectedTab,
      boatDataCache.current,
      fetchBoatData
    );

    dataPromise
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setBoatData(null);
          setSelectedBattery(0);
          setLoadedBoatKey(cacheKey);
          return;
        }
        boatDataCache.current[cacheKey] = data;
        setBoatData(data);
        setThrottle((currentThrottle) =>
          clampThrottleToDataPoints(currentThrottle, data.dataPoints)
        );
        if (data.batteries.length > 0) setSelectedBattery(data.batteries[0]);
        setLoadedBoatKey(cacheKey);
      })
      .catch((error) => {
        if (!cancelled) {
          console.error(error);
          setBoatData(null);
          setSelectedBattery(0);
          setLoadedBoatKey(cacheKey);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTab]);

  // Build range engine from boat data
  const engine = useMemo(() => {
    if (!boatData || boatData.dataPoints.length < 2) return null;
    return new RangeEngine(boatData.dataPoints);
  }, [boatData]);

  const minThrottle = engine?.minThrottle ?? 0;
  const maxThrottle = engine?.maxThrottle ?? 100;
  const effectiveThrottle = clampThrottle(throttle, minThrottle, maxThrottle);

  // Interpolated performance at current throttle
  const performance = useMemo(() => {
    if (!engine) return null;
    return engine.interpolate(effectiveThrottle, selectedBattery);
  }, [engine, effectiveThrottle, selectedBattery]);

  // Curve data for charts
  const curveData = useMemo(() => {
    if (!engine) return [];
    return engine.generateCurve(selectedBattery);
  }, [engine, selectedBattery]);

  // Route distance and remaining range
  const routeDistance = useMemo(() => {
    if (!startLocation) return 0;
    return computeRouteDistance(startLocation, waypoints);
  }, [startLocation, waypoints]);

  const remainingRange = useMemo(() => {
    const base = performance?.rangeNm ?? 0;
    return Math.max(0, base - routeDistance);
  }, [performance, routeDistance]);

  // Circle center: last waypoint, or start, or map center
  const circleCenter = useMemo(() => {
    if (waypoints.length > 0) return waypoints[waypoints.length - 1];
    if (startLocation) return startLocation;
    return mapCenter;
  }, [waypoints, startLocation, mapCenter]);

  // Build polylines (blue = reachable, red = exceeded)
  const polylines = useMemo(() => {
    if (!startLocation || waypoints.length === 0 || !performance) {
      return { blue: [], red: [] };
    }

    const baseRange = performance.rangeNm;
    const allPoints = [startLocation, ...waypoints];
    const blue: WaypointData[] = [allPoints[0]];
    const red: WaypointData[] = [];
    let remaining = baseRange;

    for (let i = 0; i < allPoints.length - 1; i++) {
      const a = allPoints[i];
      const b = allPoints[i + 1];
      const segDist = haversineNm(a.lat, a.lng, b.lat, b.lng);

      if (remaining >= segDist) {
        blue.push(b);
        remaining -= segDist;
      } else if (remaining > 0) {
        // Split segment
        const frac = remaining / segDist;
        const mid = {
          lat: a.lat + (b.lat - a.lat) * frac,
          lng: a.lng + (b.lng - a.lng) * frac,
        };
        blue.push(mid);
        red.push(mid, b);
        // Add remaining waypoints to red
        for (let j = i + 2; j < allPoints.length; j++) {
          red.push(allPoints[j]);
        }
        remaining = 0;
        break;
      } else {
        if (red.length === 0) red.push(a);
        red.push(b);
      }
    }

    return { blue, red };
  }, [startLocation, waypoints, performance]);

  // Imperatively manage polylines so they update correctly on drag
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Update or create blue polyline
    if (polylines.blue.length > 1) {
      if (!blueLineRef.current) {
        blueLineRef.current = new google.maps.Polyline({
          map,
          strokeColor: "#3B82F6",
          strokeWeight: 3,
          strokeOpacity: 0.9,
          clickable: false,
        });
      }
      blueLineRef.current.setPath(polylines.blue);
    } else {
      blueLineRef.current?.setMap(null);
      blueLineRef.current = null;
    }

    // Update or create red polyline
    if (polylines.red.length > 1) {
      if (!redLineRef.current) {
        redLineRef.current = new google.maps.Polyline({
          map,
          strokeColor: "#EF4444",
          strokeWeight: 3,
          strokeOpacity: 0.9,
          clickable: false,
        });
      }
      redLineRef.current.setPath(polylines.red);
    } else {
      redLineRef.current?.setMap(null);
      redLineRef.current = null;
    }
  }, [polylines]);

  // Clean up polylines on unmount
  useEffect(() => {
    return () => {
      blueLineRef.current?.setMap(null);
      redLineRef.current?.setMap(null);
    };
  }, []);

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const point = { lat: e.latLng.lat(), lng: e.latLng.lng() };

      if (interactionMode === "setStart") {
        setStartLocation(point);
        setWaypoints([]);
        setInteractionMode("none");
      } else if (interactionMode === "addWaypoint" && startLocation) {
        setWaypoints((prev) => [...prev, point]);
        setInteractionMode("none");
      }
    },
    [interactionMode, startLocation]
  );

  const handleReset = () => {
    setStartLocation(null);
    setWaypoints([]);
    setInteractionMode("none");
  };

  const handleUndoWaypoint = () => {
    setWaypoints((prev) => prev.slice(0, -1));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-em-blue animate-spin" />
      </div>
    );
  }

  if (tabs.length === 0 || !selectedTab) return null;

  if (loadingBoat || !boatData) {
    return (
      <DatasetLoadingState
        tabs={tabs}
        selectedTab={selectedTab}
        display={display}
        loading={loadingBoat}
        onSelect={selectBoat}
        showBoatSelector={showBoatSelector}
      />
    );
  }

  const displayRange = startLocation ? remainingRange : (performance?.rangeNm ?? 0);

  if (calculatorMode.layout === "compact") {
    return (
      <div className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          {showBoatSelector && <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Boat
            </label>
            <select
              value={selectedTab.sheetId}
              onChange={(e) => {
                const t = tabs.find((tab) => tab.sheetId === Number(e.target.value));
                if (t) selectBoat(t);
              }}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50"
            >
              {tabs.map((t) => (
                <option key={t.sheetId} value={t.sheetId}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>}

          {boatData.batteries.length > 0 && (
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Battery Capacity
              </label>
              <select
                value={selectedBattery}
                onChange={(e) => setSelectedBattery(Number(e.target.value))}
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-em-blue/50"
              >
                {boatData.batteries.map((b) => (
                  <option key={b} value={b}>
                    {formatBattery(b)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="mt-5">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Throttle - {Math.round(effectiveThrottle)}%
          </label>
          <ThrottleSlider
            minThrottle={minThrottle}
            maxThrottle={maxThrottle}
            throttle={effectiveThrottle}
            onThrottleChange={setThrottle}
            className="h-2 w-full cursor-pointer appearance-none rounded-full bg-secondary accent-[var(--color-em-blue)]"
          />
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>{Math.round(minThrottle)}%</span>
            <span>{Math.round(maxThrottle)}%</span>
          </div>
        </div>

        {performance && (
          <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <div className="rounded-lg bg-em-blue/10 p-3 lg:col-span-1">
              <div className="mb-1 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold uppercase text-em-blue">Range</span>
              </div>
              <p className="text-xl font-bold text-em-blue">{performance.rangeNm.toFixed(1)} NM</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Navigation className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">Speed</span>
              </div>
              <p className="text-lg font-bold text-foreground">{performance.speed.toFixed(1)} kts</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">Runtime</span>
              </div>
              <p className="text-lg font-bold text-foreground">{formatDuration(performance.durationHours)}</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">Power</span>
              </div>
              <p className="text-lg font-bold text-foreground">{formatWattage(performance.wattage)}</p>
            </div>
            <div className="rounded-lg bg-secondary/50 p-3">
              <div className="mb-1 flex items-center gap-1.5">
                <Gauge className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold uppercase text-muted-foreground">RPM</span>
              </div>
              <p className="text-lg font-bold text-foreground">{performance.rpm.toFixed(0)}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
      {/* Controls Panel — desktop only, mobile uses overlays */}
      <div className="hidden lg:block space-y-5 p-5 rounded-2xl bg-card border border-border">
        {/* Boat selector */}
        {showBoatSelector && <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
            Boat
          </label>
          <select
            value={selectedTab?.sheetId ?? ""}
            onChange={(e) => {
              const t = tabs.find((tab) => tab.sheetId === Number(e.target.value));
              if (t) selectBoat(t);
            }}
            className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-em-blue/50"
          >
            {tabs.map((t) => (
              <option key={t.sheetId} value={t.sheetId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>}

        {/* Battery selector */}
        {boatData && boatData.batteries.length > 0 && (
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
              Battery Capacity
            </label>
            <select
              value={selectedBattery}
              onChange={(e) => setSelectedBattery(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-em-blue/50"
            >
              {boatData.batteries.map((b) => (
                <option key={b} value={b}>
                  {formatBattery(b)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Throttle slider */}
        <div>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 block">
            Throttle — {Math.round(effectiveThrottle)}%
          </label>
          <ThrottleSlider
            minThrottle={minThrottle}
            maxThrottle={maxThrottle}
            throttle={effectiveThrottle}
            onThrottleChange={setThrottle}
            className="w-full h-2 rounded-full appearance-none bg-secondary cursor-pointer accent-[var(--color-em-blue)]"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{Math.round(minThrottle)}%</span>
            <span>{Math.round(maxThrottle)}%</span>
          </div>
        </div>

        {/* HUD Readouts */}
        {performance && !loadingBoat && (
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-secondary/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Zap className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">Power</span>
              </div>
              <p className="text-lg font-bold text-foreground">{formatWattage(performance.wattage)}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Gauge className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">RPM</span>
              </div>
              <p className="text-lg font-bold text-foreground">{performance.rpm.toFixed(0)}</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Navigation className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">Speed</span>
              </div>
              <p className="text-lg font-bold text-foreground">{performance.speed.toFixed(1)} kts</p>
            </div>
            <div className="p-3 rounded-xl bg-secondary/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold text-muted-foreground uppercase">Duration</span>
              </div>
              <p className="text-lg font-bold text-foreground">{formatDuration(performance.durationHours)}</p>
            </div>
            <div className="col-span-2 p-3 rounded-xl bg-em-blue/10">
              <div className="flex items-center gap-1.5 mb-1">
                <MapPin className="h-3.5 w-3.5 text-em-blue" />
                <span className="text-[10px] font-semibold text-em-blue uppercase">
                  {startLocation ? "Remaining Range" : "Estimated Range"}
                </span>
              </div>
              <p className="text-2xl font-bold text-em-blue">{displayRange.toFixed(1)} NM</p>
            </div>
          </div>
        )}

        {loadingBoat && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 text-em-blue animate-spin" />
          </div>
        )}

        {/* Map controls */}
        {viewMode === "map" && (
          <div className="space-y-2">
            {!startLocation ? (
              <button
                type="button"
                onClick={() => setInteractionMode("setStart")}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  interactionMode === "setStart"
                    ? "bg-em-blue text-white"
                    : "bg-secondary text-foreground hover:bg-secondary/80"
                }`}
              >
                <MapPin className="h-4 w-4" />
                {interactionMode === "setStart" ? "Click the map..." : "Set Starting Location"}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInteractionMode("addWaypoint")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    interactionMode === "addWaypoint"
                      ? "bg-em-blue text-white"
                      : "bg-secondary text-foreground hover:bg-secondary/80"
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  {interactionMode === "addWaypoint" ? "Click map..." : "Waypoint"}
                </button>
                {waypoints.length > 0 && (
                  <button
                    type="button"
                    onClick={handleUndoWaypoint}
                    className="px-3 py-2.5 rounded-lg text-sm font-medium bg-secondary text-foreground hover:bg-secondary/80 transition-all"
                    title="Undo last waypoint"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-3 py-2.5 rounded-lg text-sm font-medium bg-secondary text-foreground hover:bg-secondary/80 transition-all"
                >
                  Reset
                </button>
              </div>
            )}
          </div>
        )}

        {/* View toggle */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setViewMode("map")}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              viewMode === "map"
                ? "bg-em-blue text-white"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            <MapIcon className="h-4 w-4" />
            Map
          </button>
          <button
            type="button"
            onClick={() => setViewMode("charts")}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              viewMode === "charts"
                ? "bg-em-blue text-white"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Charts
          </button>
        </div>
      </div>

      {/* Map / Charts View */}
      <div className="relative rounded-2xl overflow-hidden border border-border bg-card">
        {viewMode === "map" ? (
          <RangeMap
            mapCenter={mapCenter}
            onMapClick={handleMapClick}
            mapRef={mapRef}
            circleCenter={circleCenter}
            displayRange={displayRange}
            startLocation={startLocation}
            setStartLocation={setStartLocation}
            waypoints={waypoints}
            setWaypoints={setWaypoints}
          />
        ) : (
          <div>
            {/* Mobile charts controls — matches map overlay styling */}
            <div className="lg:hidden bg-white/90 dark:bg-em-navy/90 backdrop-blur-md border-b border-border/50 px-4 py-2.5 space-y-2.5">
              <div className="flex gap-2 items-center">
                <button
                  type="button"
                  onClick={() => setViewMode("map")}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-black/5 dark:bg-white/10 text-foreground"
                >
                  <MapIcon className="h-3.5 w-3.5" />
                </button>
                {showBoatSelector && <select
                  value={selectedTab?.sheetId ?? ""}
                  onChange={(e) => {
                    const t = tabs.find((tab) => tab.sheetId === Number(e.target.value));
                    if (t) selectBoat(t);
                  }}
                  className="flex-1 px-2 py-1.5 rounded-lg bg-black/5 dark:bg-white/10 border-none text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-em-blue/50"
                >
                  {tabs.map((t) => (
                    <option key={t.sheetId} value={t.sheetId}>
                      {t.name}
                    </option>
                  ))}
                </select>}
                {boatData && boatData.batteries.length > 1 && (
                  <select
                    value={selectedBattery}
                    onChange={(e) => setSelectedBattery(Number(e.target.value))}
                    className="w-24 px-2 py-1.5 rounded-lg bg-black/5 dark:bg-white/10 border-none text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-em-blue/50"
                  >
                    {boatData.batteries.map((b) => (
                      <option key={b} value={b}>
                        {formatBattery(b)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {performance && (
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase whitespace-nowrap w-12">
                    {Math.round(effectiveThrottle)}%
                  </span>
                  <ThrottleSlider
                    minThrottle={minThrottle}
                    maxThrottle={maxThrottle}
                    throttle={effectiveThrottle}
                    onThrottleChange={setThrottle}
                    className="flex-1 h-2 rounded-full appearance-none bg-black/10 dark:bg-white/10 cursor-pointer accent-[var(--color-em-blue)]"
                  />
                </div>
              )}
            </div>
            <div className="p-4 sm:p-6">
              <PerformanceCharts data={curveData} currentThrottle={effectiveThrottle} />
            </div>
          </div>
        )}

        {/* Mobile overlay — top: charts button + boat/battery selectors */}
        {viewMode === "map" && !loadingBoat && (
          <div className="lg:hidden absolute top-0 left-0 right-0 bg-white/90 dark:bg-em-navy/90 backdrop-blur-md border-b border-border/50 px-4 py-2.5">
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => setViewMode("charts")}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-black/5 dark:bg-white/10 text-foreground"
                title="View charts"
              >
                <BarChart3 className="h-3.5 w-3.5" />
              </button>
              {showBoatSelector && <select
                value={selectedTab?.sheetId ?? ""}
                onChange={(e) => {
                  const t = tabs.find((tab) => tab.sheetId === Number(e.target.value));
                  if (t) selectBoat(t);
                }}
                className="flex-1 px-2 py-1.5 rounded-lg bg-black/5 dark:bg-white/10 border-none text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-em-blue/50"
              >
                {tabs.map((t) => (
                  <option key={t.sheetId} value={t.sheetId}>
                    {t.name}
                  </option>
                ))}
              </select>}
              {boatData && boatData.batteries.length > 1 && (
                <select
                  value={selectedBattery}
                  onChange={(e) => setSelectedBattery(Number(e.target.value))}
                  className="w-24 px-2 py-1.5 rounded-lg bg-black/5 dark:bg-white/10 border-none text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-em-blue/50"
                >
                  {boatData.batteries.map((b) => (
                    <option key={b} value={b}>
                      {formatBattery(b)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        )}

        {/* Mobile overlay — bottom: throttle + readouts + waypoint controls */}
        {viewMode === "map" && !loadingBoat && (
          <div className="lg:hidden absolute bottom-0 left-0 right-0 bg-white/90 dark:bg-em-navy/90 backdrop-blur-md border-t border-border/50 px-4 py-3 space-y-2.5">
            {/* Throttle slider */}
            {performance && (
              <>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase whitespace-nowrap w-12">
                    {Math.round(effectiveThrottle)}%
                  </span>
                  <ThrottleSlider
                    minThrottle={minThrottle}
                    maxThrottle={maxThrottle}
                    throttle={effectiveThrottle}
                    onThrottleChange={setThrottle}
                    className="flex-1 h-2 rounded-full appearance-none bg-black/10 dark:bg-white/10 cursor-pointer accent-[var(--color-em-blue)]"
                  />
                </div>

                {/* Key readouts row */}
                <div className="flex items-center justify-center gap-2 text-xs flex-wrap">
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-em-blue/10">
                    <MapPin className="h-3 w-3 text-em-blue" />
                    <span className="font-bold text-em-blue">{displayRange.toFixed(1)} NM</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/5 dark:bg-white/5">
                    <Navigation className="h-3 w-3 text-muted-foreground" />
                    <span className="font-semibold text-foreground">{performance.speed.toFixed(1)} kts</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/5 dark:bg-white/5">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    <span className="font-semibold text-foreground">{formatDuration(performance.durationHours)}</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-black/5 dark:bg-white/5">
                    <Zap className="h-3 w-3 text-muted-foreground" />
                    <span className="font-semibold text-foreground">{formatWattage(performance.wattage)}</span>
                  </div>
                </div>
              </>
            )}

            {/* Waypoint controls */}
            <div className="flex gap-2">
              {!startLocation ? (
                <button
                  type="button"
                  onClick={() => setInteractionMode("setStart")}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                    interactionMode === "setStart"
                      ? "bg-em-blue text-white"
                      : "bg-black/5 dark:bg-white/10 text-foreground"
                  }`}
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {interactionMode === "setStart" ? "Tap map..." : "Set Start"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setInteractionMode("addWaypoint")}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      interactionMode === "addWaypoint"
                        ? "bg-em-blue text-white"
                        : "bg-black/5 dark:bg-white/10 text-foreground"
                    }`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {interactionMode === "addWaypoint" ? "Tap map..." : "Waypoint"}
                  </button>
                  {waypoints.length > 0 && (
                    <button
                      type="button"
                      onClick={handleUndoWaypoint}
                      className="px-3 py-2 rounded-lg text-xs font-medium bg-black/5 dark:bg-white/10 text-foreground"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleReset}
                    className="px-3 py-2 rounded-lg text-xs font-medium bg-black/5 dark:bg-white/10 text-foreground"
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
